import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Users, Plus, Search, Pencil, Trash2, Loader2, ShieldCheck, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const roles = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-500/20 text-red-400', department: null },
  { value: 'group_admin', label: 'Group Admin', color: 'bg-purple-500/20 text-purple-400', department: null },
  { value: 'mall_admin', label: 'Mall Admin', color: 'bg-blue-500/20 text-blue-400', department: null },
  { value: 'ops_manager', label: 'Operations Manager', color: 'bg-orange-500/20 text-orange-400', department: 'operations' },
  { value: 'leasing_head', label: 'Leasing Head', color: 'bg-emerald-500/20 text-emerald-400', department: 'leasing' },
  { value: 'leasing_executive', label: 'Leasing Executive', color: 'bg-teal-500/20 text-teal-400', department: 'leasing' },
  { value: 'technician', label: 'Technician', color: 'bg-yellow-500/20 text-yellow-400', department: 'maintenance' },
  { value: 'security_supervisor', label: 'Security Supervisor', color: 'bg-red-500/20 text-red-400', department: 'security' },
  { value: 'security_guard', label: 'Security Guard', color: 'bg-red-400/20 text-red-300', department: 'security' },
  { value: 'housekeeping_supervisor', label: 'Housekeeping Supervisor', color: 'bg-cyan-500/20 text-cyan-400', department: 'housekeeping' },
  { value: 'housekeeping_staff', label: 'Housekeeping Staff', color: 'bg-cyan-400/20 text-cyan-300', department: 'housekeeping' },
  { value: 'finance', label: 'Finance', color: 'bg-amber-500/20 text-amber-400', department: 'finance' },
  { value: 'ops', label: 'Operations', color: 'bg-orange-400/20 text-orange-300', department: 'operations' },
  { value: 'marketing', label: 'Marketing', color: 'bg-pink-500/20 text-pink-400', department: 'marketing' },
  { value: 'advertiser', label: 'Advertiser', color: 'bg-violet-500/20 text-violet-400', department: null },
  { value: 'brand_partner', label: 'Brand Partner', color: 'bg-indigo-500/20 text-indigo-400', department: null },
  { value: 'broker', label: 'Broker', color: 'bg-lime-500/20 text-lime-400', department: null },
  { value: 'investor', label: 'Investor', color: 'bg-gray-500/20 text-gray-400', department: null },
  { value: 'tenant', label: 'Tenant', color: 'bg-green-500/20 text-green-400', department: null },
];

const departments = [
  { value: 'operations', label: 'Operations' },
  { value: 'security', label: 'Security' },
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'leasing', label: 'Leasing' },
  { value: 'finance', label: 'Finance' },
  { value: 'marketing', label: 'Marketing' },
];

const shifts = [
  { value: 'morning', label: 'Morning (6AM - 2PM)' },
  { value: 'afternoon', label: 'Afternoon (2PM - 10PM)' },
  { value: 'night', label: 'Night (10PM - 6AM)' },
  { value: 'rotating', label: 'Rotating' },
];

export default function UsersPage() {
  const { api, isSuper, isGroupAdmin, isMallAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [malls, setMalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'leasing_executive',
    group_id: '',
    mall_id: '',
    phone: '',
    department: '',
    supervisor_id: '',
    shift: '',
  });
  const [supervisors, setSupervisors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, groupsRes, mallsRes] = await Promise.all([
        api.get('/users'),
        api.get('/groups'),
        api.get('/malls'),
      ]);
      setUsers(usersRes.data);
      setGroups(groupsRes.data);
      setMalls(mallsRes.data);
      
      // Fetch supervisors if mall_id is available
      if (currentUser?.mall_id) {
        try {
          const supervisorsRes = await api.get(`/users/supervisors/${currentUser.mall_id}`);
          setSupervisors(supervisorsRes.data);
        } catch (e) {
          console.log("No supervisors found");
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, {
          name: formData.name,
          phone: formData.phone,
          role: formData.role,
          group_id: formData.group_id || null,
          mall_id: formData.mall_id || null,
          department: formData.department || null,
          supervisor_id: formData.supervisor_id || null,
          shift: formData.shift || null,
          is_active: true,
        });
        toast.success('User updated successfully');
      } else {
        await api.post('/users', formData);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      group_id: user.group_id || '',
      mall_id: user.mall_id || '',
      phone: user.phone || '',
      department: user.department || '',
      supervisor_id: user.supervisor_id || '',
      shift: user.shift || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openNewDialog = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'leasing_executive',
      group_id: currentUser?.group_id || '',
      mall_id: currentUser?.mall_id || '',
      phone: '',
      department: '',
      supervisor_id: '',
      shift: '',
    });
    setDialogOpen(true);
  };

  const getRoleColor = (role) => {
    return roles.find((r) => r.value === role)?.color || 'bg-gray-500/20 text-gray-400';
  };

  const getRoleLabel = (role) => {
    return roles.find((r) => r.value === role)?.label || role;
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const availableRoles = roles.filter((role) => {
    if (isSuper) return true;
    if (isGroupAdmin) return !['super_admin', 'group_admin'].includes(role.value);
    if (isMallAdmin) return !['super_admin', 'group_admin', 'mall_admin'].includes(role.value);
    return false;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-muted-foreground">{users.length} users in system</p>
        </div>
        <Button className="btn-primary" onClick={openNewDialog} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="search-input-wrapper flex-1 max-w-md">
          <Search className="search-icon w-4 h-4" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-dark pl-10"
            data-testid="search-users"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[180px] input-dark" data-testid="filter-role">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map((role) => (
              <SelectItem key={role.value} value={role.value}>
                {role.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="glass-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Group/Mall</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => {
                const group = groups.find((g) => g.id === user.group_id);
                const mall = malls.find((m) => m.id === user.mall_id);

                return (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {user.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={`${getRoleColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        {group && (
                          <span className="text-sm flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {group.name}
                          </span>
                        )}
                        {mall && (
                          <span className="text-xs text-muted-foreground">{mall.name}</span>
                        )}
                        {!group && !mall && <span className="text-muted-foreground">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={user.is_active ? 'default' : 'secondary'}
                        className={user.is_active ? 'bg-emerald-500/20 text-emerald-400' : ''}
                      >
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          disabled={user.id === currentUser?.id}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {(isSuper || isGroupAdmin) && user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {filteredUsers.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No users found</p>
        </div>
      )}

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass-panel sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
            <DialogDescription>
              {editingUser ? 'Update user details' : 'Create a new user account'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-dark"
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-dark"
                    required
                    disabled={!!editingUser}
                    data-testid="user-email-input"
                  />
                </div>
              </div>
              {!editingUser && (
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-dark"
                    required={!editingUser}
                    data-testid="user-password-input"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(v) => setFormData({ ...formData, role: v })}
                  >
                    <SelectTrigger className="input-dark" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-dark"
                    placeholder="+91-..."
                    data-testid="user-phone-input"
                  />
                </div>
              </div>
              {isSuper && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="group">Mall Group</Label>
                    <Select
                      value={formData.group_id || "none"}
                      onValueChange={(v) => setFormData({ ...formData, group_id: v === "none" ? "" : v, mall_id: '' })}
                    >
                      <SelectTrigger className="input-dark" data-testid="user-group-select">
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {groups.map((group) => (
                          <SelectItem key={group.id} value={group.id}>
                            {group.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mall">Mall</Label>
                    <Select
                      value={formData.mall_id || "none"}
                      onValueChange={(v) => setFormData({ ...formData, mall_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="input-dark" data-testid="user-mall-select">
                        <SelectValue placeholder="Select mall" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {malls
                          .filter((m) => !formData.group_id || m.group_id === formData.group_id)
                          .map((mall) => (
                            <SelectItem key={mall.id} value={mall.id}>
                              {mall.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {/* Staff-specific fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="department">Department</Label>
                  <Select
                    value={formData.department || "none"}
                    onValueChange={(v) => setFormData({ ...formData, department: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="input-dark" data-testid="user-department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {departments.map((dept) => (
                        <SelectItem key={dept.value} value={dept.value}>
                          {dept.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shift">Shift</Label>
                  <Select
                    value={formData.shift || "none"}
                    onValueChange={(v) => setFormData({ ...formData, shift: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="input-dark" data-testid="user-shift-select">
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {shifts.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {supervisors.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="supervisor">Reports To</Label>
                  <Select
                    value={formData.supervisor_id || "none"}
                    onValueChange={(v) => setFormData({ ...formData, supervisor_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="input-dark" data-testid="user-supervisor-select">
                      <SelectValue placeholder="Select supervisor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {supervisors.map((sup) => (
                        <SelectItem key={sup.id} value={sup.id}>
                          {sup.name} ({getRoleLabel(sup.role)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-user-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
